/**
 * フォームの定型部品。
 *
 * react-hook-form はこのアプリの依存に無いので、状態は各画面の `useState` で持ち、
 * 「送信バー」「エラー表示」だけを共通化する。検証は `@mail-sv/shared` の zod
 * スキーマ（`validation.ts`）を使い、UI 側で正規表現を再定義しない。
 */
import * as React from 'react';
import { Alert, AlertDescription, AlertTitle, Button } from '@mail-sv/ui';
import { ApiError, errorMessage } from '../lib/api.js';

/** ダイアログ / フォーム下部の送信バー。送信中はキャンセルも止める。 */
export function SubmitBar({
  submitLabel = '保存する',
  cancelLabel = 'キャンセル',
  pending = false,
  disabled = false,
  onCancel,
  destructive = false,
  hint,
}: {
  submitLabel?: string;
  cancelLabel?: string;
  pending?: boolean;
  disabled?: boolean;
  onCancel?: () => void;
  destructive?: boolean;
  hint?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
      {hint ? <p className="mr-auto text-xs text-muted-foreground">{hint}</p> : null}
      {onCancel ? (
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          {cancelLabel}
        </Button>
      ) : null}
      <Button
        type="submit"
        variant={destructive ? 'destructive' : 'default'}
        loading={pending}
        disabled={disabled}
      >
        {submitLabel}
      </Button>
    </div>
  );
}

/**
 * mutation のエラー表示。
 * 権限エラーだけは「なぜ出来ないか」が分かる文言に差し替える（§11.2）。
 */
export function FormAlert({ error }: { error: unknown }) {
  if (!error) return null;
  const isForbidden = error instanceof ApiError && error.status === 403;
  return (
    <Alert variant="destructive">
      <AlertTitle>{isForbidden ? '権限がありません' : '実行できませんでした'}</AlertTitle>
      <AlertDescription>
        {isForbidden
          ? 'この操作は組織の管理者だけが行えます。管理者にご依頼ください。'
          : errorMessage(error)}
      </AlertDescription>
    </Alert>
  );
}

/** 入力の下に出す文字数カウンタなど、右寄せの補助テキスト。 */
export function FieldHint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}

/** 単純な検証結果の型。各画面は `validate()` の戻り値をこの形にそろえる。 */
export type FieldErrors<K extends string> = Partial<Record<K, string>>;

/** 送信前に呼ぶ。エラーが 1 件でもあれば true。 */
export function hasErrors<K extends string>(errors: FieldErrors<K>): boolean {
  return Object.values(errors).some((v) => typeof v === 'string' && v.length > 0);
}
